/**
 * API Client Registry
 *
 * Provides pre-configured API client instances based on environment
 * configuration. This centralizes the Real-vs-Mock decision and keeps
 * stores and components decoupled from the implementation selection logic.
 *
 * Selection Logic:
 * 1. If VITE_USE_MOCKS=true, use Mock implementation for all domains
 * 2. Otherwise, check the domain's backendReady flag:
 *    - If backendReady is true, use Real implementation
 *    - If backendReady is false, use Mock implementation and emit console warning
 *
 * @module api/registry
 */

import type { AuthApiClient } from './AuthApiClient';
import type { UsersApiClient } from './UsersApiClient';
import type { OrgsApiClient } from './OrgsApiClient';
import type { ExternalAccessApiClient } from './ExternalAccessApiClient';
import { RealExternalAccessApiClient } from './ExternalAccessApiClient';
import type { TenantsApiClient } from './TenantsApiClient';
import { RealTenantsApiClient } from './TenantsApiClient';
import { RealAuthApiClient } from './AuthApiClient';
import { RealUsersApiClient } from './UsersApiClient';
import { RealOrgsApiClient } from './OrgsApiClient';
import {
  MockAuthApiClient,
  type MockAuthApiClientConfig,
} from './AuthApiClient.mock';
import {
  MockUsersApiClient,
  type MockUsersApiClientConfig,
} from './UsersApiClient.mock';
import {
  MockOrgsApiClient,
  type MockOrgsApiClientConfig,
} from './OrgsApiClient.mock';
import {
  MockExternalAccessApiClient,
  type MockExternalAccessApiClientConfig,
} from './ExternalAccessApiClient.mock';
import { isGlobalMockMode } from '../config';

// =============================================================================
// Types
// =============================================================================

/**
 * Domain identifiers for API clients.
 */
export type ApiDomain = 'auth' | 'users' | 'orgs' | 'externalAccess';

/**
 * Backend readiness configuration for each domain.
 */
export interface BackendReadyConfig {
  readonly auth: boolean;
  readonly users: boolean;
  readonly orgs: boolean;
  readonly externalAccess: boolean;
}

// =============================================================================
// Configuration
// =============================================================================

/**
 * Backend readiness flags for each domain.
 *
 * These are compile-time constants that indicate whether the backend
 * for each domain is deployed and functional.
 *
 * - auth: true (BFF at /oauth-agent/* is deployed)
 * - users: true (tenant-management backend at /api/tenant-management/team/*
 *   is deployed and reached through the oidc-bff /api/* proxy)
 * - orgs: true (staff-admin org list at /api/tenant-management/orgs is deployed;
 *   the OrgPicker calls the real backend. Per the "no mocks in previews"
 *   directive, the picker shows an honest loading/empty/error state when the
 *   backend is unreachable rather than falling back to fixture data.)
 *
 * All domains are now backend-ready: no mock fallback is active in deployed
 * environments. (Mocks remain available for local dev via VITE_USE_MOCKS and
 * for unit/E2E tests via injected clients.)
 */
export const BACKEND_READY: BackendReadyConfig = {
  auth: true,
  users: true,
  orgs: true,
  externalAccess: true,
};

// =============================================================================
// Warning State
// =============================================================================

/**
 * Tracks which domains have already emitted a fallback warning.
 * Used to ensure warnings are only emitted once per page load.
 */
const warnedDomains: Set<ApiDomain> = new Set();

/**
 * Emit a console warning when a Mock is used due to backendReady=false.
 *
 * The warning:
 * - Indicates which domain is using mocks
 * - Explains that the backend is not yet wired up
 * - Only appears once per domain per page load
 *
 * @param domain - The domain that is falling back to mocks
 */
function emitFallbackWarning(domain: ApiDomain): void {
  if (warnedDomains.has(domain)) {
    return;
  }

  warnedDomains.add(domain);

  const domainDescriptions: Record<ApiDomain, string> = {
    auth: 'BFF Token Handler at /oauth-agent/*',
    users: 'ZITADEL Management API proxy at /api/*',
    orgs: 'staff-admin org list at /api/tenant-management/orgs',
    externalAccess:
      'external access at /api/tenant-management/orgs/{orgId}/users/external',
  };

  console.warn(
    `[API Registry] Using mock implementation for "${domain}" domain. ` +
      `The ${domainDescriptions[domain]} is not yet wired up. ` +
      'This is expected during development.'
  );
}

/**
 * Reset warning state. Used for testing.
 * @internal
 */
export function _resetWarningState(): void {
  warnedDomains.clear();
}

// =============================================================================
// Client Factory Functions
// =============================================================================

/**
 * Determine which implementation to use for a given domain.
 *
 * @param domain - The API domain
 * @returns 'mock' if Mock should be used, 'real' if Real should be used
 */
export function getImplementationType(domain: ApiDomain): 'mock' | 'real' {
  // Global mock mode takes precedence
  if (isGlobalMockMode()) {
    return 'mock';
  }

  // Check domain-specific backend readiness
  return BACKEND_READY[domain] ? 'real' : 'mock';
}

/**
 * Create an Auth API client based on configuration.
 *
 * @returns AuthApiClient instance (Real or Mock)
 */
function createAuthClient(): AuthApiClient {
  const implType = getImplementationType('auth');

  if (implType === 'mock') {
    // Only emit warning if this is a fallback (not global mock mode)
    if (!isGlobalMockMode()) {
      emitFallbackWarning('auth');
    }
    const e2eConfig = readE2EAuthConfig();
    const mock = new MockAuthApiClient(e2eConfig);
    exposeMocksOnWindow({ auth: mock });
    return mock;
  }

  return new RealAuthApiClient();
}

/**
 * Create a Users API client based on configuration.
 *
 * @returns UsersApiClient instance (Real or Mock)
 */
function createUsersClient(): UsersApiClient {
  const implType = getImplementationType('users');

  if (implType === 'mock') {
    // Only emit warning if this is a fallback (not global mock mode)
    if (!isGlobalMockMode()) {
      emitFallbackWarning('users');
    }
    // Disable the mock's internal warning since we handle it here
    const e2eConfig = readE2EUsersConfig();
    const mock = new MockUsersApiClient({ emitWarning: false, ...e2eConfig });
    exposeMocksOnWindow({ users: mock });
    return mock;
  }

  return new RealUsersApiClient();
}

/**
 * Create an Orgs API client based on configuration.
 *
 * @returns OrgsApiClient instance (Real or Mock)
 */
function createOrgsClient(): OrgsApiClient {
  const implType = getImplementationType('orgs');

  if (implType === 'mock') {
    // Only emit warning if this is a fallback (not global mock mode)
    if (!isGlobalMockMode()) {
      emitFallbackWarning('orgs');
    }
    const e2eConfig = readE2EOrgsConfig();
    const mock = new MockOrgsApiClient({ emitWarning: false, ...e2eConfig });
    exposeMocksOnWindow({ orgs: mock });
    return mock;
  }

  return new RealOrgsApiClient();
}

/**
 * Create an External Access API client based on configuration.
 *
 * @returns ExternalAccessApiClient instance (Real or Mock)
 */
function createExternalAccessClient(): ExternalAccessApiClient {
  const implType = getImplementationType('externalAccess');

  if (implType === 'mock') {
    if (!isGlobalMockMode()) {
      emitFallbackWarning('externalAccess');
    }
    const e2eConfig = readE2EExternalAccessConfig();
    const mock = new MockExternalAccessApiClient(e2eConfig);
    exposeMocksOnWindow({ externalAccess: mock });
    return mock;
  }

  return new RealExternalAccessApiClient();
}

// =============================================================================
// E2E Test Bridge
// =============================================================================

/**
 * Bridge object attached to `window.__MP_MOCKS__` so Playwright tests can
 * configure mock client behavior at runtime via `page.evaluate(...)`.
 */
interface MockBridge {
  auth?: MockAuthApiClient;
  users?: MockUsersApiClient;
  orgs?: MockOrgsApiClient;
  externalAccess?: MockExternalAccessApiClient;
}

/**
 * Shape of the global object exposed on window for E2E test control.
 *
 * When the app is running with mocks enabled, the mock client instances are
 * attached to `window.__MP_MOCKS__` so Playwright tests can configure mock
 * behavior at runtime (e.g., trigger seat limit errors, swap claims).
 *
 * Tests configure pre-boot state via init scripts that set
 * `window.__E2E_AUTH_MOCK_CONFIG__` and `window.__E2E_USERS_MOCK_CONFIG__`
 * before the app's modules execute.
 */
declare global {
  interface Window {
    __E2E_AUTH_MOCK_CONFIG__?: MockAuthApiClientConfig;
    __E2E_USERS_MOCK_CONFIG__?: MockUsersApiClientConfig;
    __E2E_ORGS_MOCK_CONFIG__?: MockOrgsApiClientConfig;
    __E2E_EXTERNAL_ACCESS_MOCK_CONFIG__?: MockExternalAccessApiClientConfig;
    __MP_MOCKS__?: MockBridge;
  }
}

/**
 * SSR-safe accessor for the browser `window`. Returns `undefined` when this
 * module is evaluated outside a browser (e.g., during unit tests using jsdom
 * teardown or future server-side rendering).
 */
function getWindow(): Window | undefined {
  return typeof window === 'undefined' ? undefined : window;
}

/**
 * Read the auth mock configuration injected by E2E tests via window.
 * Returns undefined when not in a browser or when no config is provided.
 */
function readE2EAuthConfig(): MockAuthApiClientConfig | undefined {
  return getWindow()?.__E2E_AUTH_MOCK_CONFIG__;
}

/**
 * Read the users mock configuration injected by E2E tests via window.
 */
function readE2EUsersConfig(): MockUsersApiClientConfig | undefined {
  return getWindow()?.__E2E_USERS_MOCK_CONFIG__;
}

/**
 * Read the orgs mock configuration injected by E2E tests via window.
 */
function readE2EOrgsConfig(): MockOrgsApiClientConfig | undefined {
  return getWindow()?.__E2E_ORGS_MOCK_CONFIG__;
}

/**
 * Read the external-access mock configuration injected by E2E tests via window.
 */
function readE2EExternalAccessConfig():
  | MockExternalAccessApiClientConfig
  | undefined {
  return getWindow()?.__E2E_EXTERNAL_ACCESS_MOCK_CONFIG__;
}

/**
 * Merge mock client instances into `window.__MP_MOCKS__` so E2E tests can
 * call configuration methods directly via `page.evaluate(...)`. No-op when
 * not running in a browser.
 */
function exposeMocksOnWindow(mocks: MockBridge): void {
  const win = getWindow();
  if (!win) {
    return;
  }
  win.__MP_MOCKS__ = { ...win.__MP_MOCKS__, ...mocks };
}

// =============================================================================
// Singleton Instances
// =============================================================================

/**
 * Singleton auth client instance.
 *
 * Uses RealAuthApiClient when:
 * - VITE_USE_MOCKS is not "true" AND auth.backendReady is true
 *
 * Uses MockAuthApiClient when:
 * - VITE_USE_MOCKS is "true" (no warning)
 * - OR auth.backendReady is false (emits warning once)
 *
 * @example
 * ```typescript
 * import { authClient } from './api/registry';
 *
 * const claims = await authClient.getClaims();
 * ```
 */
export const authClient: AuthApiClient = createAuthClient();

/**
 * Singleton users client instance.
 *
 * Uses RealUsersApiClient when:
 * - VITE_USE_MOCKS is not "true" AND users.backendReady is true
 *
 * Uses MockUsersApiClient when:
 * - VITE_USE_MOCKS is "true" (no warning)
 * - OR users.backendReady is false (emits warning once)
 *
 * @example
 * ```typescript
 * import { usersClient } from './api/registry';
 *
 * const { items } = await usersClient.listUsers('org-123', { page: 1, pageSize: 10 });
 * ```
 */
export const usersClient: UsersApiClient = createUsersClient();

/**
 * Singleton orgs client instance.
 *
 * Uses RealOrgsApiClient when:
 * - VITE_USE_MOCKS is not "true" AND orgs.backendReady is true
 *
 * Uses MockOrgsApiClient when:
 * - VITE_USE_MOCKS is "true" (no warning)
 * - OR orgs.backendReady is false (emits warning once)
 *
 * Feeds the staff-admin org picker (see useOrgsStore).
 *
 * @example
 * ```typescript
 * import { orgsClient } from './api/registry';
 *
 * const { orgs } = await orgsClient.listOrgs();
 * ```
 */
export const orgsClient: OrgsApiClient = createOrgsClient();

/**
 * Singleton external-access client instance (EPIC-AUTH-008).
 *
 * Goes through the registry rather than being constructed directly, so the
 * External access section behaves under VITE_USE_MOCKS and in E2E the way every
 * other domain does. It is rendered on the members tab, i.e. on every visit, so
 * a client that always reached the network would fire a real request in every
 * mock-mode session.
 */
export const externalAccessClient: ExternalAccessApiClient =
  createExternalAccessClient();

/**
 * Singleton tenants client instance (tenant create / details / delete).
 *
 * Always the Real implementation: tenant lifecycle is a privileged, backend-
 * enforced operation with no mock fallback in deployed environments. Unit tests
 * inject a fake TenantsApiClient into components directly rather than through the
 * registry, so no mock/E2E bridge is wired here.
 */
export const tenantsClient: TenantsApiClient = new RealTenantsApiClient();
